import { useCallback, useEffect, useMemo, useState } from 'react';
import { populationRecordsApi } from '../api/populationRecordsApi';
import { marketsApi } from '../api/marketsApi';
import { facilitiesApi } from '../api/facilitiesApi';
import { conflictsApi } from '../api/conflictsApi';
import { livestockActorsApi } from '../api/workforceApi';
import { workplansApi } from '../api/planningApi';
import { animalRegistrationsApi } from '../api/animalRegistrationsApi';
import { outbreakRecordsApi } from '../api/animalHealthApi';
import { useCollectionSync } from './useCollectionSync';

const COUNT_PAGE_SIZE = 1;

function countCollection(result) {
  const pagination = result?.pagination ?? result?.meta?.pagination ?? null;
  const total = Number(
    pagination?.total ??
    pagination?.count ??
    pagination?.total_records ??
    result?.total ??
    result?.count ??
    result?.total_records
  );
  if (Number.isFinite(total)) return total;
  if (Array.isArray(result?.items)) return result.items.length;
  if (Array.isArray(result?.results)) return result.results.length;
  if (Array.isArray(result)) return result.length;
  return 0;
}

async function fetchCount(request, fallback = 0) {
  try {
    const result = await request();
    const count = countCollection(result);
    return Number.isFinite(count) ? count : fallback;
  } catch {
    return fallback;
  }
}

const FETCHERS = {
  'livestock-population':    () => fetchCount(() => populationRecordsApi.list({ per_page: COUNT_PAGE_SIZE })),
  'markets-trade':           () => fetchCount(() => marketsApi.list({ per_page: COUNT_PAGE_SIZE })),
  'infrastructure':          () => fetchCount(() => facilitiesApi.list({ per_page: COUNT_PAGE_SIZE })),
  'workforce-institutional': () => fetchCount(() => livestockActorsApi.list({ per_page: COUNT_PAGE_SIZE })),
  'planning-reporting':      () => fetchCount(() => workplansApi.list({ per_page: COUNT_PAGE_SIZE })),
  'conflicts-dispute':       () => fetchCount(() => conflictsApi.records.list({ per_page: COUNT_PAGE_SIZE })),
  'animal-identification':   () => fetchCount(() => animalRegistrationsApi.list({ per_page: COUNT_PAGE_SIZE })),
  'animal-health':           () => fetchCount(() => outbreakRecordsApi.list({ per_page: COUNT_PAGE_SIZE })),
};

export function useModuleCounts(moduleIds = null) {
  const requestedKey = Array.isArray(moduleIds) && moduleIds.length > 0
    ? moduleIds.join('|')
    : Object.keys(FETCHERS).join('|');
  const activeModuleIds = useMemo(
    () => [...new Set(requestedKey.split('|').filter(Boolean))].filter((id) => FETCHERS[id]),
    [requestedKey]
  );
  const [counts, setCounts] = useState(() =>
    Object.fromEntries(activeModuleIds.map((id) => [id, 0]))
  );

  const refreshCounts = useCallback(() => {
    let mounted = true;
    const entries = activeModuleIds.map((id) => [id, FETCHERS[id]]);

    Promise.all(
      entries.map(async ([id, fetchCountFn]) => [id, await fetchCountFn()])
    ).then((results) => {
      if (!mounted) return;
      setCounts(
        Object.fromEntries(
          results.map(([id, value]) => [id, Number.isFinite(Number(value)) ? Number(value) : 0])
        )
      );
    });

    return () => { mounted = false; };
  }, [activeModuleIds]);

  useEffect(() => {
    setCounts(Object.fromEntries(activeModuleIds.map((id) => [id, 0])));
  }, [activeModuleIds]);

  useEffect(() => {
    const cancel = refreshCounts();
    return () => {
      if (typeof cancel === 'function') cancel();
    };
  }, [refreshCounts]);

  useCollectionSync([
    populationRecordsApi.collectionKey,
    marketsApi.collectionKey,
    facilitiesApi.collectionKey,
    livestockActorsApi.collectionKey,
    workplansApi.collectionKey,
    animalRegistrationsApi.collectionKey,
    outbreakRecordsApi.collectionKey,
    'vaccination-records',
    'mortality-records',
    conflictsApi.records.collectionKey,
  ], refreshCounts);

  return counts;
}
